import { SystemElement, SystemConnectionType } from "./Connectivity";
import { DtFacility } from "../DtFacility";
import { DtConstants } from "../schema/DtConstants";
import { OUTSIDE, INTERSECTS, CONTAINS, geomIntersectsNDC } from "../../wgs/scene/FrustumIntersector";
import { KeyFlags } from "../schema/dt-schema";
import { TandemCategories } from "../schema/TandemCategories";
import * as THREE from "three";

const _color = new THREE.Color(0x000000);
const tmpMat = new THREE.Matrix4();
const tmpV3 = new THREE.Vector3();
const tmpBox = new THREE.Box3();
const boxArr = new Array(6);

/**
 * Returns a list of elements' dbIds, grouped by DtModel
 * @param {SystemElement[]} elements
 * @returns {Map<DtModel, int[]>} dbIdsByModel
 */
function getElementsDbIdsByModel(elements) {
  const perModel = new Map();

  for (const element of elements) {
    const model = element.model;
    const dbId = element.dbId;

    let dbIds = perModel.get(model);
    if (!dbIds) {
      dbIds = [];
      perModel.set(model, dbIds);
    }

    dbIds.push(dbId);
  }

  return perModel;
}

/**
 * Returns a list of elements and their connections' dbIds, grouped by modelURN
 * @param {SystemElement[]} elements
 * @returns {{ urn: [dbIds]}}
 */
function getElementsDbIdsByURN(elements) {
  const dbIdsByURN = {};

  const appendElement = (element) => {
    const urn = element.model.urn();
    const perModel = dbIdsByURN[urn] = dbIdsByURN[urn] ?? new Set();
    perModel.add(element.dbId);
  };

  for (const element of elements) {
    appendElement(element);

    for (const conn of element.allConnections()) {
      appendElement(conn);
    }
  }

  return dbIdsByURN;
}

/**
 * Filter all elements using a System filter and group their dbIds by URN
 * @param {DtFacility} facility
 * @returns {{ urn: [dbIds]}}
 */
function getFilteredDbIdsByURN(facility, filter) {

  const dbIdsByURN = {};
  const system = { filter };

  for (const model of facility.getModels()) {

    const dbIds = dbIdsByURN[model.urn()] = [];
    // Contains all dbIds
    const { dbId2flags } = model.getData();
    for (let dbId = 1; dbId < dbId2flags.length; ++dbId) {

      if (dbId2flags[dbId] === DtConstants.ElementFlags.System) continue;
      if (dbId2flags[dbId] === DtConstants.ElementFlags.Deleted) continue;

      // TODO: Add filter checks here when we allow more system filters
      // ex: if (!isSystemElement && /*SOME_CHECK*/) { isSystemElement = true; };

      if (isSystemElement(system, model, dbId)) {
        dbIds.push(dbId);
      }
    }
  }

  return dbIdsByURN;
}

/**
 * Count the number of elements that match a given system's filter
 * @param {DtFacility} facility
 * @param {System} system
 */
function countSystemElements(facility, system) {

  system.elementsCount = 0;

  const dbIdsByURN = getFilteredDbIdsByURN(facility, system.filter);
  for (const urn in dbIdsByURN) {
    system.elementsCount += dbIdsByURN[urn].length;
  }

  return system.elementsCount;
}

/**
 * Check whether filtered system elements contain center lines
 * @param {DtFacility} facility
 * @param {*} filter
 * @returns {boolean}
 */
function hasCenterlines(facility, filter) {
  const dbIdsByURN = getFilteredDbIdsByURN(facility, filter);

  for (const urn in dbIdsByURN) {
    const model = facility.getModelByUrn(urn);
    const modelData = model.getData();
    const dbId2catId = modelData.dbId2catId;

    for (const dbId of dbIdsByURN[urn]) {
      if (dbId2catId[dbId] in DtConstants.CenterlineCatIDs) {
        return true;
      }
    }
  }

  return false;
}

function isSystemElement(system, model, dbId) {
  const { dbId2SystemClass } = model.getData();
  const { systemClasses } = system.filter;
  // Both system and element classes are sets/bitmasks. Element is part of the system if there is overlap.
  return systemClasses & dbId2SystemClass[dbId];
}

function colorElement(element, color) {
  if (color instanceof THREE.Color) {
    _color.copy(color);
  } else {
    _color.set(color);
  }

  element.model.setThemingColor(element.dbId, new THREE.Vector4(_color.r, _color.g, _color.b, 1));
}

/**
 * Must invalidate after to see color change
 */
function colorElements(elements, color) {
  for (const element of elements) {
    colorElement(element, color);
  }
}

function aggregateIsolateElements(viewer, elements) {
  const dbIdsByModel = getElementsDbIdsByModel(elements);
  const selection = [];

  for (const [model, dbIds] of dbIdsByModel) {
    if (model.selector) {
      selection.push({ model, ids: dbIds });
    }
  }

  viewer.impl.visibilityManager.aggregateIsolate(selection);
}

/**
 * Set aggregate selection on a list of elements
 * @param {SystemElement[]} elements
 */
function aggregateSelectElements(viewer, elements = []) {
  const dbIdsByModel = getElementsDbIdsByModel(elements);
  const selection = [];

  for (const [model, dbIds] of dbIdsByModel) {
    if (model.selector) {
      selection.push({ model, ids: dbIds });
    }
  }

  viewer.setAggregateSelection(selection);
}

/**
 * @param {SystemElement[]} elements
 * @returns {boolean} true if connection exists between all elements within a list of elements
 */
function allConnected(elements = []) {
  for (let i = 0; i < elements.length - 1; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      if (elements[i].getConnectionType(elements[j]) === null) {
        return false;
      }
    }
  }

  return true;
}

function disconnectMultiple(elements = []) {
  for (let i = 0; i < elements.length - 1; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      removeConnection(elements[i], elements[j]);
    }
  }
}

function colorAttributeSubsystems(viewer, system) {
  const subsystems = Array.from(system.subsystems.values().map(({ elements }) => elements));

  _colorSubsystem(viewer, subsystems);
}

/**
 * Color group elements by sub-systems
 * IE. groups of elements where every node has a path to every other node
 */
function colorConnectedSubsystems(viewer, system) {
  const subsystems = getSubSystems(system);

  _colorSubsystem(viewer, subsystems);
}

function _colorSubsystem(viewer, list) {
  // Dsc sort by size to ensure semi consistent coloring
  list.sort((a, b) => b.length - a.length);

  const colorGen = new HighContrastScaledColorGenerator(list[0].length, viewer.impl.isDarkBackground());

  for (const elements of list) {
    colorElements(elements, colorGen.next(elements.length));
  }

  viewer.refresh(true);
}

/**
 * Generate high contrast colors that are mutually distinguishable.
 * Lightness is scaled for each color by the ratio between a given number and a maximum.
 */
class HighContrastScaledColorGenerator {
  #magicPrime = 67;
  #isCompl = true;
  #initH;
  #initS;

  /**
   * @param {Number} maxSize Maximum number of elements in a group
   * @param {boolean} isDark Whether the background is dark, defaults to false
   * @param {Number} h Hue value (0 - 359) for the first color, defaults to 225
   * @param {Number} s Saturation value (percentage) for the first color, defaults to 75
   * @param {Number} lMin Minimum lightness value (percentage), defaults to 20
   * @param {Number} lMax Maximum lightness value (percentage), defaults to 80
   */
  constructor(maxSize, isDark, h = 225, s = 75, lMin = 20, lMax = 80) {
    this.isDark = !!isDark;
    this.maxSize = maxSize;

    this.#initH = this.h = h;
    this.#initS = this.s = s;

    this.lMin = lMin;
    this.lMax = lMax;
    this.lRange = this.lMax - this.lMin;

    // Log scale for lightness such that the largest group of elements has maximum lightness
    this.logScale = this.lRange / Math.log(maxSize + 1);
  }

  reset() {
    this.#isCompl = true;
    this.h = this.#initH;
    this.s = this.#initS;
  }

  next(groupSize) {
    // Offset lightness depending on number of elements in group and background
    const lOffset = this.logScale * Math.log(groupSize + 1);
    const lightness = this.isDark ? this.lMax - lOffset : this.lMin + lOffset;

    const color = _color.setHSL(this.h / 360, this.s / 100, lightness / 100).clone();

    this.#next();

    return color;
  }

  #next() {
    // Determine next hue/saturation
    if (this.#isCompl) {
      // Use complementary color
      this.h += 180;
      this.h %= 360;
    } else {
      // Use new distinguishable
      this.h += this.#magicPrime - 180;

      if (this.h < 0 || this.h >= 360) {
        // Avoid negatives
        this.h = (this.h + 360) % 360;
        // Decrease saturation on a full turn of the color wheel
        this.s -= 10;
        // but not too much
        if (this.s < 25) this.s = 75;
      }
    }
    // Alternate
    this.#isCompl = !this.#isCompl;
  }
}

/**
 * Remove connection between elements
 * @param {SystemElement} element1
 * @param {SystemElement} element2
 */
function removeConnection(element1, element2) {
  element1.disconnect(element2);
  element2.disconnect(element1);
}

/**
 * Set connection between elements
 * @param {SystemElement} prev
 * @param {SystemElement} next
 * @param {(prev: SystemElement, next: SystemElement)} propagate
 */
function createConnection(prev, next, propagate) {
  prev.connect(next, SystemConnectionType.Next);
  next.connect(prev, SystemConnectionType.Previous);

  if (propagate instanceof Function) {
    propagate(prev, next);
  }
}

function _getElementsRecursive(element, direction, acc = new Set()) {
  if (acc.has(element)) {
    return;
  }

  acc.add(element);

  element.enumConnections(direction, (_, conn) => _getElementsRecursive(conn, direction, acc));
}

/**
 * Get all elements connected to start element, regardless of connection type.
 * @param {SystemElement} element Start element
 * @param {boolean} inclusiveSelect To include start element in the results
 * @returns {[SystemElement]}
 */
function getConnectedElements(element, inclusiveSelect = false) {
  let elements = new Set();
  _getElementsRecursive(element, null, elements);

  if (!inclusiveSelect) {
    elements.delete(element);
  }

  return [...elements];
}

/**
 * @returns {Array<Set<SystemElement>>} Sub systems
 */
function getSubSystems(system) {
  const subSystems = [];

  for (const element of system.allElements()) {
    if (element.visited) {
      continue;
    }

    const subSystem = [];
    element.bfs((node) => {
      node.visited = true;
      subSystem.push(node);
    }, false);

    subSystems.push(subSystem);
  }

  system.clearVisitedFlag();

  return subSystems;
}

function getNearby(facility, system, element, includeGhosted = true) {
  let models = facility.getModels();

  tmpBox.makeEmpty();
  // Get facility bbox size to expand search box
  for (const model of models) {
    const modelBox = model.getBoundingBox(true);
    tmpBox.union(modelBox);
  }

  const targetModel = element.model;
  const targetDbId = element.dbId;
  const targetBox = new THREE.Box3();
  const targetIT = targetModel.getInstanceTree();

  targetIT.getNodeBox(targetDbId, boxArr);
  targetBox.min.set(boxArr[0], boxArr[1], boxArr[2]);
  targetBox.max.set(boxArr[3], boxArr[4], boxArr[5]);

  // Expand search box by 0.5% of the facility size
  tmpBox.getSize(tmpV3).multiplyScalar(0.005);
  targetBox.expandByVector(tmpV3);

  let res = [];

  for (let model of models) {

    const fl = model.getFragmentList();
    const mData = model.getData();

    let ids = new Set();

    const customIntersector = {
      intersectsBox: function (box) {
        if (targetBox.containsBox(box)) {
          return CONTAINS;
        } else if (targetBox.intersectsBox(box)) {
          return INTERSECTS;
        } else {
          return OUTSIDE;
        }
      }
    };

    // intersectFrustum already ensures that frag-bbox clips target-bbox prior to callback
    model.getIterator().intersectFrustum(customIntersector, (fragId, containmentKnown) => {
      const dbId = fl.getDbIds(fragId);

      if (model === targetModel && dbId === targetDbId || ids.has(dbId)) {
        return;
      }

      // Ignore system elements
      if (system.filter.systemClasses & mData.dbId2SystemClass[dbId]) {
        return;
      }

      // Ignore logical elements
      if (mData.dbId2flags[dbId] & KeyFlags.Logical) {
        return;
      }

      // ignore everything that's not MEP or eQuipment
      if (!TandemCategories.Range.MEPQ.includes(mData.dbId2TandemCategory[dbId])) {
        return;
      }

      // Frag-bbox is entirely contained by scaled target-bbox, so close enough
      if (containmentKnown) {
        ids.add(dbId);
        return;
      }

      // Frag geom itself clips or is contained by target-bbox
      const geom = fl.getGeometry(fragId);
      fl.getWorldMatrix(fragId, tmpMat);

      if (geomIntersectsNDC(geom, tmpMat, targetBox)) {
        ids.add(dbId);
      }
    }, includeGhosted);

    if (ids.size) {
      res.push({ model, ids: [...ids] });
    }
  }

  return res;
}

export const ConnectivityUtils = {
  getConnectedElements,
  getSubSystems,
  getElementsDbIdsByModel,
  getElementsDbIdsByURN,
  getFilteredDbIdsByURN,
  countSystemElements,
  hasCenterlines,
  isSystemElement,

  createConnection,
  removeConnection,
  disconnectMultiple,

  colorElement,
  colorElements,
  colorAttributeSubsystems,
  colorConnectedSubsystems,

  aggregateIsolateElements,
  aggregateSelectElements,

  allConnected,

  getNearby
};